/**
 * Turn URLs and email addresses in PLAIN TEXT into something clickable (#369).
 *
 * HTML bodies already arrive with real anchors and are handled by the sanitizer. This is
 * for the other half: forwarded staff mail and text/plain form notifications, where a
 * Webflow upload link or a homeowner's own website is just characters on screen and the
 * only way to follow it is to select and copy it by hand.
 *
 * Deliberately NOT a general HTML linkifier. It returns segments and lets the caller decide
 * how to render them, because the same split is wanted in React Native `<Text>` (where an
 * `<a>` does not exist) and would be an injection risk if it emitted markup.
 */

export type LinkSegment =
	| { kind: 'text'; value: string }
	| { kind: 'link'; value: string; href: string };

/**
 * Matches, in order: a scheme'd URL, a bare `www.` host, an email address.
 *
 * `www.` is included because homeowners write it that way constantly and a link that only
 * works with `https://` typed in front of it is not a link. Everything else is left alone —
 * guessing that "example.com" is a URL turns ordinary sentences ("see attached, thanks.co")
 * into false links.
 */
const PATTERN = /(https?:\/\/[^\s<>[\]"']+)|(\bwww\.[^\s<>[\]"']+)|([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/gi;

/**
 * Trailing punctuation that belongs to the sentence, not the link.
 *
 * Parentheses are deliberately INSIDE the URL character class above — plenty of real links
 * end in one, `…/wiki/Foo_(bar)` being the canonical case — so the balance check in
 * `trimTrailing` is what separates those from a URL merely wrapped in prose parens.
 */
const TRAILING_JUNK = new Set(['.', ',', ';', ':', '!', '?', ')', ']', '}', "'", '"']);

/**
 * Balance-aware trim: a Wikipedia-style URL genuinely ends in `)`, so only strip a closing
 * paren the URL never opened.
 */
function trimTrailing(raw: string): { value: string; trailing: string } {
	let value = raw;
	let trailing = '';
	while (value.length > 0) {
		const last = value[value.length - 1];
		if (!TRAILING_JUNK.has(last)) break;
		if (last === ')') {
			const opens = (value.match(/\(/g) ?? []).length;
			const closes = (value.match(/\)/g) ?? []).length;
			// The paren closes one the URL opened — keep it.
			if (opens >= closes) break;
		}
		trailing = last + trailing;
		value = value.slice(0, -1);
	}
	return { value, trailing };
}

/** Split text into plain and linkable segments, in order, losing nothing. */
export function linkifySegments(text: string): LinkSegment[] {
	if (!text) return [];
	const segments: LinkSegment[] = [];
	let cursor = 0;

	for (const match of text.matchAll(PATTERN)) {
		const start = match.index ?? 0;
		const { value, trailing } = trimTrailing(match[0]);
		if (!value) continue;

		if (start > cursor) segments.push({ kind: 'text', value: text.slice(cursor, start) });

		const href = value.includes('@') && !value.startsWith('http')
			? `mailto:${value}`
			: value.startsWith('www.') ? `https://${value}` : value;
		segments.push({ kind: 'link', value, href });

		if (trailing) segments.push({ kind: 'text', value: trailing });
		cursor = start + match[0].length;
	}

	if (cursor < text.length) segments.push({ kind: 'text', value: text.slice(cursor) });
	return segments;
}

/** True when the text has at least one thing worth making clickable. */
export function hasLinks(text: string | null | undefined): boolean {
	return !!text && linkifySegments(text).some((s) => s.kind === 'link');
}
